import { CalendarDays, Check, ChevronDown, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import {
  DASHBOARD_PERIOD_OPTIONS,
  createDashboardPeriodState,
  dashboardPeriodDisplayRange,
  type DashboardPeriodState,
} from "@/features/dashboard/period";
import type {
  DashboardPeriodKind,
  DashboardPeriodMetadata,
} from "@/features/dashboard/types";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { isInclusiveISODateRange } from "@/lib/dates";
import "./DashboardLedger.css";

export interface DashboardPeriodControlProps {
  value?: DashboardPeriodState;
  pendingValue?: DashboardPeriodState | null;
  metadata?: DashboardPeriodMetadata;
  now?: Date;
  onChange?: (period: DashboardPeriodState) => void;
}

type FloatingLayer = "mobile-period" | "desktop-custom" | null;
type MobileStep = "period" | "custom";

const FLOATING_DURATION_MS = 160;
const DESKTOP_RANGE_PLACEHOLDERS: Record<DashboardPeriodKind, string> = {
  today: "00/00",
  week: "00/00 a 00/00",
  month: "00/0000",
  custom: "00/00 a 00/00",
  all: "00/00/0000 a 00/00/0000",
};

const requestFrame = (callback: FrameRequestCallback): number => {
  const schedule =
    window.requestAnimationFrame ?? globalThis.requestAnimationFrame;
  if (schedule) return schedule(callback);
  return window.setTimeout(() => callback(performance.now()), 0);
};

const cancelFrame = (frame: number) => {
  const cancel = window.cancelAnimationFrame ?? globalThis.cancelAnimationFrame;
  if (cancel) {
    cancel(frame);
    return;
  }
  window.clearTimeout(frame);
};

const compactDate = (value: string) =>
  `${value.slice(8, 10)}/${value.slice(5, 7)}`;

const fullDate = (value: string) =>
  `${compactDate(value)}/${value.slice(0, 4)}`;

function metadataDisplayRange(metadata: DashboardPeriodMetadata): string {
  if (metadata.kind === "all") {
    if (!metadata.from) return `Até ${fullDate(metadata.to)}`;
    if (metadata.from === metadata.to) return fullDate(metadata.to);
    return `${fullDate(metadata.from)} a ${fullDate(metadata.to)}`;
  }
  if (metadata.kind === "month") {
    return `${metadata.to.slice(5, 7)}/${metadata.to.slice(0, 4)}`;
  }
  if (metadata.from === metadata.to) return compactDate(metadata.to);
  return `${compactDate(metadata.from)} a ${compactDate(metadata.to)}`;
}

function periodRange(
  state: DashboardPeriodState,
  metadata: DashboardPeriodMetadata | undefined,
  now: Date | undefined,
): string {
  if (state.period === "all") {
    return metadata?.kind === "all" ? metadataDisplayRange(metadata) : "";
  }
  if (
    metadata?.kind === state.period &&
    (state.period !== "custom" ||
      (metadata.from === state.from && metadata.to === state.to))
  ) {
    return metadataDisplayRange(metadata);
  }
  if (state.period === "custom" && (!state.from || !state.to)) return "";
  return dashboardPeriodDisplayRange(state, now);
}

function triggerLabel(label: string, range: string): string {
  return range ? `Período: ${label}, ${range}` : `Período: ${label}`;
}

function dashboardPeriodStatesEqual(
  left: DashboardPeriodState,
  right: DashboardPeriodState,
): boolean {
  if (left.period !== right.period || left.history !== right.history) {
    return false;
  }
  if (left.period !== "custom") return true;
  return left.from === right.from && left.to === right.to;
}

function useAnchoredPosition(
  open: boolean,
  anchor: HTMLElement | null,
  layerRef: RefObject<HTMLDivElement | null>,
) {
  const [style, setStyle] = useState<CSSProperties>({});
  const frameRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!open || !anchor) return;

    const update = () => {
      frameRef.current = null;
      const rect = anchor.getBoundingClientRect();
      const visualViewport = window.visualViewport;
      const viewportLeft = visualViewport?.offsetLeft ?? 0;
      const viewportTop = visualViewport?.offsetTop ?? 0;
      const viewportWidth = visualViewport?.width ?? window.innerWidth;
      const viewportHeight = visualViewport?.height ?? window.innerHeight;
      const viewportRight = viewportLeft + viewportWidth;
      const viewportBottom = viewportTop + viewportHeight;
      const width = Math.min(364, Math.max(0, viewportWidth - 28));
      const maxHeight = Math.max(0, viewportHeight - 28);
      const measuredHeight = layerRef.current?.getBoundingClientRect().height;
      const height = Math.min(
        measuredHeight && measuredHeight > 0 ? measuredHeight : 214,
        maxHeight,
      );
      const left = Math.min(
        Math.max(viewportLeft + 14, rect.right - width),
        Math.max(viewportLeft + 14, viewportRight - width - 14),
      );
      const below = rect.bottom + 8;
      const above = rect.top - height - 8;
      const top = Math.min(
        Math.max(
          viewportTop + 14,
          below + height <= viewportBottom - 14 ? below : above,
        ),
        Math.max(viewportTop + 14, viewportBottom - height - 14),
      );
      setStyle({ left, top, width, maxHeight });
    };

    const scheduleUpdate = () => {
      if (frameRef.current !== null) return;
      frameRef.current = requestFrame(update);
    };

    update();
    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("scroll", scheduleUpdate, true);
    window.visualViewport?.addEventListener("resize", scheduleUpdate);
    window.visualViewport?.addEventListener("scroll", scheduleUpdate);
    return () => {
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("scroll", scheduleUpdate, true);
      window.visualViewport?.removeEventListener("resize", scheduleUpdate);
      window.visualViewport?.removeEventListener("scroll", scheduleUpdate);
      if (frameRef.current !== null) cancelFrame(frameRef.current);
    };
  }, [anchor, layerRef, open]);

  return style;
}

export function DashboardPeriodControl({
  value,
  pendingValue,
  metadata,
  now,
  onChange,
}: DashboardPeriodControlProps) {
  const [internalValue, setInternalValue] = useState(() =>
    createDashboardPeriodState(),
  );
  const [floatingLayer, setFloatingLayer] = useState<FloatingLayer>(null);
  const [floatingVisible, setFloatingVisible] = useState(false);
  const [floatingClosing, setFloatingClosing] = useState(false);
  const [floatingAnchor, setFloatingAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [mobileStep, setMobileStep] = useState<MobileStep>("period");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const desktopOptionsRef = useRef<Array<HTMLButtonElement | null>>([]);
  const mobileOptionsRef = useRef<Array<HTMLButtonElement | null>>([]);
  const mobileCustomOptionRef = useRef<HTMLButtonElement | null>(null);
  const mobileTriggerRef = useRef<HTMLButtonElement>(null);
  const floatingRef = useRef<HTMLDivElement>(null);
  const customFromRef = useRef<HTMLInputElement>(null);
  const openFrameRef = useRef<number | null>(null);
  const restoreFrameRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const isMobile = useMediaQuery("(max-width: 640px)");
  const previousIsMobileRef = useRef(isMobile);
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const selection = value ?? internalValue;
  const activeRange = useMemo(
    () => periodRange(selection, metadata, now),
    [metadata, now, selection],
  );
  const activeOption =
    DASHBOARD_PERIOD_OPTIONS.find(
      ({ period }) => period === selection.period,
    ) ?? DASHBOARD_PERIOD_OPTIONS[2];
  const pendingOption = pendingValue
    ? !dashboardPeriodStatesEqual(selection, pendingValue)
      ? DASHBOARD_PERIOD_OPTIONS.find(
          ({ period }) => period === pendingValue.period,
        )
      : undefined
    : undefined;
  const pendingRange =
    pendingValue && pendingOption
      ? periodRange(pendingValue, undefined, now)
      : "";
  const floatingStyle = useAnchoredPosition(
    floatingLayer === "desktop-custom",
    floatingAnchor,
    floatingRef,
  );

  const showFloating = useCallback(
    (layer: Exclude<FloatingLayer, null>, anchor: HTMLElement) => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      if (openFrameRef.current !== null) cancelFrame(openFrameRef.current);
      if (restoreFrameRef.current !== null) {
        cancelFrame(restoreFrameRef.current);
        restoreFrameRef.current = null;
      }
      if (layer === "mobile-period") setMobileStep("period");
      setFloatingVisible(false);
      setFloatingClosing(false);
      setFloatingAnchor(anchor);
      setFloatingLayer(layer);
      openFrameRef.current = requestFrame(() => {
        openFrameRef.current = null;
        setFloatingVisible(true);
      });
    },
    [],
  );

  const closeFloating = useCallback(
    (shouldRestoreFocus = true, deferFocus = false, immediate = false) => {
      const layer = floatingLayer;
      const anchor = floatingAnchor;
      if (openFrameRef.current !== null) {
        cancelFrame(openFrameRef.current);
        openFrameRef.current = null;
      }
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setFloatingVisible(false);
      if (reducedMotion || immediate) {
        setFloatingLayer(null);
        setFloatingClosing(false);
      } else {
        setFloatingClosing(true);
        closeTimerRef.current = window.setTimeout(() => {
          closeTimerRef.current = null;
          setFloatingLayer(null);
          setFloatingClosing(false);
        }, FLOATING_DURATION_MS);
      }

      if (layer !== "mobile-period" && shouldRestoreFocus) {
        if (restoreFrameRef.current !== null)
          cancelFrame(restoreFrameRef.current);
        if (deferFocus) {
          restoreFrameRef.current = requestFrame(() => {
            restoreFrameRef.current = null;
            anchor?.focus();
          });
        } else {
          anchor?.focus();
        }
      }
    },
    [floatingAnchor, floatingLayer, reducedMotion],
  );

  useEffect(() => {
    const previousIsMobile = previousIsMobileRef.current;
    previousIsMobileRef.current = isMobile;
    if (previousIsMobile === isMobile) return;

    const incompatibleLayer = isMobile ? "desktop-custom" : "mobile-period";
    if (floatingLayer === incompatibleLayer) {
      closeFloating(false, false, true);
    }
  }, [closeFloating, floatingLayer, isMobile]);

  useDialogFocus(floatingRef, closeFloating, {
    enabled: isMobile && floatingLayer === "mobile-period" && !floatingClosing,
    restoreTarget: mobileTriggerRef.current,
  });

  const commit = useCallback(
    (next: DashboardPeriodState) => {
      setInternalValue(next);
      onChange?.(next);
    },
    [onChange],
  );

  const prepareCustomDates = useCallback(() => {
    setCustomFrom(selection.period === "custom" ? (selection.from ?? "") : "");
    setCustomTo(selection.period === "custom" ? (selection.to ?? "") : "");
  }, [selection]);

  const choosePeriod = useCallback(
    (
      period: DashboardPeriodKind,
      anchor: HTMLElement,
      fromSheet = false,
      keepSheetOpen = false,
    ) => {
      if (period === "custom") {
        prepareCustomDates();
        if (fromSheet) {
          setMobileStep("custom");
        } else {
          showFloating("desktop-custom", anchor);
        }
        return;
      }

      commit(
        createDashboardPeriodState({ period, history: selection.history }),
      );
      if (fromSheet && !keepSheetOpen) closeFloating();
    },
    [
      closeFloating,
      commit,
      prepareCustomDates,
      selection.history,
      showFloating,
    ],
  );

  const onRadioKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    index: number,
    refs: Array<HTMLButtonElement | null>,
    fromSheet: boolean,
  ) => {
    let nextIndex: number | undefined;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (index + 1) % DASHBOARD_PERIOD_OPTIONS.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex =
        (index - 1 + DASHBOARD_PERIOD_OPTIONS.length) %
        DASHBOARD_PERIOD_OPTIONS.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = DASHBOARD_PERIOD_OPTIONS.length - 1;
    }

    if (nextIndex === undefined) return;
    event.preventDefault();
    const next = refs[nextIndex];
    const nextPeriod = DASHBOARD_PERIOD_OPTIONS[nextIndex]?.period;
    next?.focus();
    if (next && nextPeriod) choosePeriod(nextPeriod, next, fromSheet, true);
  };

  useEffect(() => {
    if (
      floatingLayer !== "desktop-custom" ||
      floatingClosing ||
      !floatingVisible
    ) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeFloating();
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (floatingRef.current?.contains(target)) return;
      if (floatingAnchor?.contains(target)) return;
      closeFloating(false);
    };
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (floatingRef.current?.contains(target)) return;
      if (floatingAnchor?.contains(target)) return;
      closeFloating(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [
    closeFloating,
    floatingAnchor,
    floatingClosing,
    floatingLayer,
    floatingVisible,
  ]);

  useEffect(() => {
    if (
      floatingLayer !== "desktop-custom" ||
      !floatingVisible ||
      floatingClosing
    ) {
      return;
    }
    const frame = requestFrame(() => customFromRef.current?.focus());
    return () => cancelFrame(frame);
  }, [floatingClosing, floatingLayer, floatingVisible]);

  useEffect(() => {
    if (
      floatingLayer !== "mobile-period" ||
      mobileStep !== "custom" ||
      !floatingVisible ||
      floatingClosing
    ) {
      return;
    }
    const frame = requestFrame(() => customFromRef.current?.focus());
    return () => cancelFrame(frame);
  }, [floatingClosing, floatingLayer, floatingVisible, mobileStep]);

  useEffect(
    () => () => {
      if (openFrameRef.current !== null) cancelFrame(openFrameRef.current);
      if (restoreFrameRef.current !== null)
        cancelFrame(restoreFrameRef.current);
      if (closeTimerRef.current !== null)
        window.clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const validCustomRange = isInclusiveISODateRange(customFrom, customTo);

  const options = (refs: Array<HTMLButtonElement | null>, fromSheet: boolean) =>
    DASHBOARD_PERIOD_OPTIONS.map((option, index) => {
      const checked = option.period === selection.period;
      const pending = option.period === pendingOption?.period;
      const range = checked ? activeRange : "";
      const desktopPopup = !fromSheet && option.period === "custom";
      return (
        <button
          type="button"
          role="radio"
          aria-checked={checked}
          aria-current={checked ? "true" : undefined}
          aria-busy={pending ? "true" : undefined}
          aria-haspopup={desktopPopup ? "dialog" : undefined}
          aria-expanded={
            desktopPopup
              ? floatingLayer === "desktop-custom" &&
                !floatingClosing &&
                floatingAnchor === refs[index]
              : undefined
          }
          aria-label={range ? `${option.label}, ${range}` : option.label}
          className={`dashboard-ledger__period-option${
            fromSheet ? " dashboard-ledger__sheet-option" : ""
          }`}
          data-pending={pending ? "true" : undefined}
          tabIndex={checked ? 0 : -1}
          key={option.period}
          ref={(element) => {
            refs[index] = element;
            if (fromSheet && option.period === "custom") {
              mobileCustomOptionRef.current = element;
            }
          }}
          onKeyDown={(event) => onRadioKeyDown(event, index, refs, fromSheet)}
          onClick={(event) =>
            choosePeriod(option.period, event.currentTarget, fromSheet)
          }
        >
          <span className="dashboard-ledger__period-name">{option.label}</span>
          {fromSheet ? (
            range ? (
              <span className="dashboard-ledger__period-range">{range}</span>
            ) : null
          ) : (
            <span
              className="dashboard-ledger__period-range"
              data-visible={range ? "true" : "false"}
              title={range || undefined}
              aria-hidden={range ? undefined : "true"}
            >
              {range || DESKTOP_RANGE_PLACEHOLDERS[option.period]}
            </span>
          )}
          {fromSheet && checked ? (
            <Check
              className="dashboard-ledger__sheet-check"
              size={18}
              strokeWidth={2.4}
              aria-hidden="true"
            />
          ) : null}
          {pending ? (
            <span
              className="dashboard-ledger__period-pending-dot"
              aria-hidden="true"
            />
          ) : null}
        </button>
      );
    });

  const customFields = (
    <div className="dashboard-ledger__custom-fields">
      <label className="dashboard-ledger__date-field">
        <span>De</span>
        <input
          ref={customFromRef}
          type="date"
          value={customFrom}
          onInput={(event) => setCustomFrom(event.currentTarget.value)}
        />
      </label>
      <label className="dashboard-ledger__date-field">
        <span>Até</span>
        <input
          type="date"
          value={customTo}
          onInput={(event) => setCustomTo(event.currentTarget.value)}
        />
      </label>
    </div>
  );

  const applyCustom = () => {
    if (!validCustomRange) return;
    commit(
      createDashboardPeriodState({
        period: "custom",
        from: customFrom,
        to: customTo,
        history: selection.history,
      }),
    );
    closeFloating();
  };

  const floating =
    floatingLayer && typeof document !== "undefined"
      ? createPortal(
          floatingLayer === "desktop-custom" ? (
            <div
              ref={floatingRef}
              className="dashboard-ledger dashboard-ledger__floating dashboard-ledger__custom-popover"
              style={floatingStyle}
              role="dialog"
              aria-modal="false"
              aria-label="Período personalizado"
              aria-hidden={floatingClosing || undefined}
              data-floating="anchored"
              data-state={floatingVisible ? "open" : "closed"}
            >
              <div className="dashboard-ledger__floating-header">
                <h2 className="dashboard-ledger__floating-title">
                  Período personalizado
                </h2>
                <button
                  type="button"
                  className="dashboard-ledger__floating-close"
                  aria-label="Fechar"
                  onClick={() => closeFloating()}
                >
                  <X size={18} aria-hidden="true" />
                </button>
              </div>
              {customFields}
              <div className="dashboard-ledger__custom-actions">
                <button
                  type="button"
                  className="dashboard-ledger__custom-action"
                  onClick={() => closeFloating()}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="dashboard-ledger__custom-action dashboard-ledger__custom-action--apply"
                  disabled={!validCustomRange}
                  onClick={applyCustom}
                >
                  Aplicar
                </button>
              </div>
            </div>
          ) : (
            <div
              className="dashboard-ledger dashboard-ledger__period-sheet-overlay"
              data-state={floatingVisible ? "open" : "closed"}
              aria-hidden={floatingClosing || undefined}
            >
              <button
                type="button"
                className="dashboard-ledger__period-sheet-backdrop"
                aria-label="Fechar seleção de período"
                tabIndex={-1}
                onClick={() => closeFloating()}
              />
              <div
                ref={floatingRef}
                className="dashboard-ledger__period-bottom-sheet"
                role="dialog"
                aria-modal="true"
                aria-label={
                  mobileStep === "period"
                    ? "Escolher período"
                    : "Período personalizado"
                }
                tabIndex={-1}
              >
                <span
                  className="dashboard-ledger__sheet-handle"
                  aria-hidden="true"
                />
                <div className="dashboard-ledger__floating-header">
                  <h2 className="dashboard-ledger__floating-title">
                    {mobileStep === "period"
                      ? "Escolher período"
                      : "Período personalizado"}
                  </h2>
                  <button
                    type="button"
                    className="dashboard-ledger__floating-close"
                    aria-label="Fechar"
                    onClick={() => closeFloating()}
                  >
                    <X size={19} aria-hidden="true" />
                  </button>
                </div>
                {mobileStep === "period" ? (
                  <div
                    className="dashboard-ledger__sheet-options"
                    role="radiogroup"
                    aria-label="Período dos indicadores no celular"
                  >
                    {options(mobileOptionsRef.current, true)}
                  </div>
                ) : (
                  <>
                    {customFields}
                    <div className="dashboard-ledger__custom-actions dashboard-ledger__custom-actions--mobile">
                      <button
                        type="button"
                        className="dashboard-ledger__custom-action"
                        onClick={() => {
                          setMobileStep("period");
                          requestFrame(() =>
                            mobileCustomOptionRef.current?.focus(),
                          );
                        }}
                      >
                        Voltar
                      </button>
                      <button
                        type="button"
                        className="dashboard-ledger__custom-action dashboard-ledger__custom-action--apply"
                        disabled={!validCustomRange}
                        onClick={applyCustom}
                      >
                        Aplicar
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ),
          document.body,
        )
      : null;

  return (
    <div className="dashboard-ledger dashboard-ledger__period-control">
      <div
        className="dashboard-ledger__desktop-period"
        role="radiogroup"
        aria-label="Período dos indicadores"
      >
        {options(desktopOptionsRef.current, false)}
      </div>
      <button
        ref={mobileTriggerRef}
        type="button"
        className="dashboard-ledger__mobile-period-trigger"
        aria-label={triggerLabel(activeOption.label, activeRange)}
        aria-haspopup="dialog"
        aria-expanded={
          floatingLayer === "mobile-period" &&
          !floatingClosing &&
          floatingVisible
        }
        onClick={(event) => {
          if (floatingLayer === "mobile-period" && floatingVisible) {
            closeFloating();
            return;
          }
          showFloating("mobile-period", event.currentTarget);
        }}
      >
        <span
          className="dashboard-ledger__mobile-period-icon"
          aria-hidden="true"
        >
          <CalendarDays size={18} />
        </span>
        <span className="dashboard-ledger__mobile-period-copy">
          <span className="dashboard-ledger__mobile-period-name">
            {activeOption.label}
          </span>
          <span className="dashboard-ledger__mobile-period-range">
            {activeRange || "Período atual"}
          </span>
        </span>
        <ChevronDown
          className="dashboard-ledger__mobile-period-chevron"
          size={19}
          aria-hidden="true"
        />
      </button>
      {pendingOption ? (
        <span
          className="dashboard-ledger__sr-only"
          role="status"
          aria-live="polite"
        >
          Carregando {pendingOption.label}
          {pendingRange ? `, ${pendingRange}` : ""}
        </span>
      ) : null}
      {floating}
    </div>
  );
}
